import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { Generation, Modeconstraint } from '@api-consent/schema/Enums.js';
import { LitElementExtended } from '@src/classes/LitElementExtended.js';
import '@src/components/tef-coms-consent.js';
import { service } from '@src/service/index.js';
import {
  TRACKER_EVENT_NAME_CLICK_COLLAPSABLE,
  TRACKER_EVENT_NAME_START_CHANGE_PERMISSION,
} from '@src/service/tracker.js';
import {
  deny, oneclick, setActiveGroupInd, setCollapsibleOpen,
} from '@src/store/consents.js';
import { store } from '@src/store/index.js';
import { ConsentGroup } from '@src/types/common.js';
import { haveUncheckedFeatures } from '@src/util/consents.js';

@customElement('tef-coms-consent-group')
export class TefComsConsentGroup extends connect(store)(LitElementExtended) {
  @property({ type: Object }) consentGroup?: ConsentGroup = undefined;
  @property({ type: Number }) groupIndex: number = -1;
  @property({ type: String }) mode: string = '';

  @state() generation: Generation = Generation.CLASSIC;

  constructor() {
    super();
    const { consents: { generation } } = store.getState();
    this.generation = generation ?? 1;

    // Events in OCCL bubble
    this.addEventListener('occl.collapsible.open', () => {
      this.oneClickOpenHandler();
    });
  }

  oneClickOpenHandler(groupName?: string) {
    service.tracker.doTracking(TRACKER_EVENT_NAME_CLICK_COLLAPSABLE, {
      group: groupName ?? this.groupName,
      subject: this.mode && this.mode.includes('multiedit') ? 'consent' : 'oneclick',
    });
    store.dispatch(setCollapsibleOpen(this.groupName));
  }

  createRenderRoot() {
    return this; // no shadow DOM for sub-components
  }

  firstUpdated() {
    // For tefOne the event is only triggered on the one-accordion, which must already be rendered.
    // https://one.o9.de/main/global/docs/index.html?path=/docs/one-accordion-item--docs
    // @ts-expect-error "set by tefOne on window"
    if (typeof window.one !== 'undefined') {
      this.renderRoot.querySelectorAll('one-accordion-item').forEach((accordionItem$) => {
        accordionItem$.addEventListener('after-show', () => { this.oneClickOpenHandler(this.groupName); });
      });
    }
  }

  updated() {
    const { oneclick: isOneclick, consents } = this.consentGroup as ConsentGroup;

    // calculate oneclick
    if (isOneclick && !this.mode.includes('multiedit')) {
      // calculate oneclick checkbox: true only if all granted
      const inGroupIsSomethingUnchecked = haveUncheckedFeatures(consents)
        || !!consents.find((c) => !c.granted);
      const oneclickCheck = this.querySelector(`#oneclick_check_${this.groupIndex}`) as HTMLInputElement;
      oneclickCheck.checked = !inGroupIsSomethingUnchecked;
    }

    this.replaceElements();
  }

  get noConsentSet() {
    return !(this.consentGroup as ConsentGroup).consents.find((co) => co.granted);
  }

  oneclickAction() {
    const oneclickCheck = this.querySelector(`#oneclick_check_${this.groupIndex}`) as HTMLInputElement;
    const { checked } = oneclickCheck;
    store.dispatch(oneclick(this.groupIndex, checked));
  }

  denyAction() {
    const denyCheck = this.querySelector(`#deny_check_${this.groupIndex}`) as HTMLInputElement;
    const { checked } = denyCheck;
    store.dispatch(deny(this.groupIndex, checked));
  }

  get groupOneclickText() {
    const checkBoxId = `oneclick_check_${this.groupIndex}`;

    const clickTextTemplate = this.getTemplateFn('clickTextTemplate');

    return clickTextTemplate(checkBoxId, this);
  }

  get groupText() {
    const { text } = this.consentGroup as ConsentGroup;

    return text ? html` ${unsafeHTML(text)} ` : nothing;
  }

  get groupName() {
    return this.consentGroup?.name;
  }

  get consentPrefix() {
    return this.consentGroup?.consentPrefix;
  }

  get groupOneclickConsentsContainer() {
    const { config: { stylecontext = '' }, consents: { collapsibleOpenGroupName } } = store.getState();

    const clickConsentsContainerTemplate = this.getTemplateFn('clickConsentsContainerTemplate');

    return clickConsentsContainerTemplate(stylecontext, this, collapsibleOpenGroupName);
  }

  groupConsents(layout = 'default') {
    const { consents, required, displayMode } = this.consentGroup as ConsentGroup;

    // "required" or displayMode="list" => render as list
    if (required || this.isDisplayModeList(displayMode)) {
      const groupConsentsTemplate = this.getTemplateFn('groupConsentsTemplate');
      return [groupConsentsTemplate(this)];
    }

    const displayConsents = consents.filter((consent) => !consent.deprecated && consent.modeconstraint !== Modeconstraint.VIEW);
    return displayConsents.map((consent) => html`
      <tef-coms-consent
        .consent=${consent}
        ?required=${required}
        .layout=${layout}
        .displayMode=${displayMode}
        data-testid=${consent.id}>
      </tef-coms-consent>`);
  }

  get withdrawal() {
    const { denyall, oneclick: denyOneclick } = this.consentGroup as ConsentGroup;

    if (!denyall || denyOneclick) {
      return null;
    }

    const checkboxId = `deny_check_${this.groupIndex}`;

    const withdrawalTemplate = this.getTemplateFn('withdrawalTemplate');

    return withdrawalTemplate(this, checkboxId);
  }

  openGroupForEdit(e: MouseEvent) {
    store.dispatch(setActiveGroupInd(this.groupIndex));
    service.tracker.doTracking(TRACKER_EVENT_NAME_START_CHANGE_PERMISSION, {
      group: this.groupName,
    });
    e.preventDefault();
  }

  get viewTemplate() {
    const {
      consents: { consentGroupsView },
      config: { stylecontext = '' },
    } = store.getState();

    const { consents } = consentGroupsView![this.groupIndex];

    const filteredConsents = consents
      .filter(({ granted = false }) => granted === true)
      .filter(({ modeconstraint }) => modeconstraint !== Modeconstraint.EDIT);

    const groupViewTemplate = this.getTemplateFn('groupViewTemplate');

    return groupViewTemplate(this, consentGroupsView, filteredConsents, stylecontext);
  }

  get template() {
    return this.getTemplateFn('groupTemplate')(this);
  }

  render() {
    const isMulteditMode = this.mode && this.mode.includes('multiedit');

    if (isMulteditMode) {
      return this.viewTemplate;
    }

    return this.template;
  }
}
